import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "permissionsModal", "permUserId", "permUserName", "permUserEmail",
    "permAvatarInitials", "permPermissions", "permRole", "permGroupAdmin",
    "regionSection", "regionSelect", "regionTags", "regionIds",
    "permSaveBtn", "deleteModal",
    "restoreModal", "restoreName", "restoreEmail", "restoreConfirmBtn"
  ]

  // ── Open Modal ──────────────────────────────────────────────────────

  async openPermissionsModal(event) {
    const btn = event.currentTarget
    this._currentCompanyId = btn.dataset.userCompanyId
    this.permUserIdTarget.value = btn.dataset.userId
    this.permUserNameTarget.textContent = btn.dataset.userName
    this.permUserEmailTarget.textContent = btn.dataset.userEmail
    this.permAvatarInitialsTarget.textContent = btn.dataset.userInitials || ""

    this.permPermissionsTarget.value = btn.dataset.userReadOnly === "true" ? "view_only" : "edit"
    this.permRoleTarget.value = btn.dataset.userRole || "user"
    if (this.hasPermGroupAdminTarget) {
      this.permGroupAdminTarget.checked = btn.dataset.userGroupAdmin === "true"
    }

    // Reset region state
    this.regionTagsTarget.innerHTML = ""
    this.regionIdsTarget.value = ""
    this._regionsLoaded = false
    this._toggleRegionSection()

    // If regional_access, load regions then populate tags
    if (btn.dataset.userRole === "regional_access") {
      await this._loadRegions()
      if (btn.dataset.userRegionIds) {
        const ids = btn.dataset.userRegionIds.split(",")
        const names = btn.dataset.userRegionNames ? btn.dataset.userRegionNames.split("|||") : []
        ids.forEach((id, i) => {
          if (id) this._addRegionTag(id, names[i] || `Region ${id}`)
        })
      }
    }

    this.permissionsModalTarget.classList.remove("hidden")
  }

  closePermissionsModal() {
    this.permissionsModalTarget.classList.add("hidden")
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) this.closePermissionsModal()
  }

  // ── Role change → show/hide region section ─────────────────────────

  async onRoleChange() {
    this._toggleRegionSection()
    if (this.permRoleTarget.value === "regional_access" && !this._regionsLoaded) {
      await this._loadRegions()
    }
  }

  _toggleRegionSection() {
    if (this.permRoleTarget.value === "regional_access") {
      this.regionSectionTarget.classList.remove("hidden")
    } else {
      this.regionSectionTarget.classList.add("hidden")
      this.regionTagsTarget.innerHTML = ""
      this.regionIdsTarget.value = ""
    }
  }

  // ── Load regions for the user's company ────────────────────────────

  async _loadRegions() {
    const select = this.regionSelectTarget
    select.innerHTML = `<option value="">Select...</option>`

    try {
      const response = await fetch(`/admin/users/regions?company_id=${this._currentCompanyId}`, {
        headers: { "Accept": "application/json" }
      })
      if (response.ok) {
        const regions = await response.json()
        regions.forEach(r => {
          const opt = document.createElement("option")
          opt.value = r.id
          opt.dataset.name = r.name
          opt.textContent = r.name
          select.appendChild(opt)
        })
        this._regionsLoaded = true
      }
    } catch {
      // silent
    }
  }

  // ── Region tags ────────────────────────────────────────────────────

  addRegion() {
    const select = this.regionSelectTarget
    const id = select.value
    if (!id) return

    const name = select.options[select.selectedIndex].dataset.name
    const current = this.regionIdsTarget.value ? this.regionIdsTarget.value.split(",") : []
    if (current.includes(id)) { select.value = ""; return }

    this._addRegionTag(id, name)
    select.value = ""
  }

  _addRegionTag(id, name) {
    const tag = document.createElement("span")
    tag.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand/10 text-brand text-[13px] font-medium"
    tag.dataset.regionId = id
    tag.innerHTML = `${name}<button type="button" class="ml-0.5 hover:text-red-500 cursor-pointer" data-action="click->admin--users#removeRegion">&times;</button>`
    this.regionTagsTarget.appendChild(tag)
    this._syncRegionIds()
  }

  removeRegion(event) {
    event.currentTarget.closest("[data-region-id]").remove()
    this._syncRegionIds()
  }

  _syncRegionIds() {
    const ids = [...this.regionTagsTarget.querySelectorAll("[data-region-id]")].map(el => el.dataset.regionId)
    this.regionIdsTarget.value = ids.join(",")
  }

  // ── Save ───────────────────────────────────────────────────────────

  async submitPermissions() {
    const btn = this.permSaveBtnTarget
    const userId = this.permUserIdTarget.value
    const url = `/admin/users/${userId}/update_permission`

    btn.disabled = true
    btn.textContent = "Saving..."

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          user_role: this.permRoleTarget.value,
          user_permissions: this.permPermissionsTarget.value,
          group_admin: this.hasPermGroupAdminTarget && this.permGroupAdminTarget.checked,
          region_ids: this.regionIdsTarget.value
        })
      })

      if (response.ok) {
        const data = await response.json()
        this._updateRoleCell(userId, data.role, data.read_only, data.role_name)
        this.closePermissionsModal()
        this._showToast("Permissions updated successfully.")
      } else {
        // e.g. "Keep at least one Admin" (pmfit K6-7) — say why.
        const data = await response.json().catch(() => ({}))
        this._showToast((data.errors && data.errors[0]) || "Could not update permissions.", "alert")
      }
    } catch {
      this._showToast("Could not update permissions.", "alert")
    } finally {
      btn.disabled = false
      btn.textContent = "Save Changes"
    }
  }

  _updateRoleCell(userId, role, readOnly, roleName) {
    const btn = this.element.querySelector(`[data-user-id="${userId}"]`)
    if (!btn) return

    const cell = btn.closest("td")
    if (!cell) return

    const label = roleName || role.replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    const colorMap = {
      commander: "bg-red-100 text-red-700",
      captain: "bg-blue-100 text-blue-700",
      regional_access: "bg-teal-100 text-teal-700",
      "user-pm": "bg-amber-100 text-amber-700",
      "user-bidding-pm": "bg-orange-100 text-orange-700",
      officer: "bg-purple-100 text-purple-700"
    }
    const colors = colorMap[role] || "bg-gray-100 text-gray-600"
    const lockIcon = readOnly ? `<svg class="w-3 h-3 opacity-60" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/></svg>` : ""

    // Update badge inside the button
    btn.innerHTML = `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium ${colors}">${label}${lockIcon}</span>`

    // Update data attributes for next open
    btn.dataset.userRole = role
    btn.dataset.userReadOnly = readOnly
  }

  // ── Restore Modal ─────────────────────────────────────────────────

  showRestoreModal(event) {
    const btn = event.currentTarget
    this._restoreUrl = btn.dataset.restoreUrl
    this._restoreUserId = btn.dataset.userId
    this.restoreNameTarget.textContent = btn.dataset.userName
    this.restoreEmailTarget.textContent = btn.dataset.userEmail
    this.restoreModalTarget.classList.remove("hidden")
  }

  hideRestoreModal() {
    this.restoreModalTarget.classList.add("hidden")
  }

  async confirmRestore() {
    const btn = this.restoreConfirmBtnTarget
    btn.disabled = true
    btn.textContent = "Restoring..."

    try {
      const response = await fetch(this._restoreUrl, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        this._removeDeletedRow(this._restoreUserId)
        this.hideRestoreModal()
        this._showToast("User restored successfully.")
      } else {
        this.hideRestoreModal()
        this._showToast("Could not restore this user. Please try again.", "alert")
      }
    } catch {
      this.hideRestoreModal()
      this._showToast("Could not restore this user. Please try again.", "alert")
    } finally {
      btn.disabled = false
      btn.textContent = "Restore"
    }
  }

  _removeDeletedRow(userId) {
    const row = this.element.querySelector(`tr[data-deleted-user-row][data-user-id="${userId}"]`)
    if (!row) return

    const tbody = row.closest("tbody")
    row.remove()

    if (tbody && !tbody.querySelector("tr[data-deleted-user-row]")) {
      const tr = document.createElement("tr")
      tr.innerHTML = `<td colspan="5" class="text-center py-8 text-sm text-text-muted">No deleted users.</td>`
      tbody.appendChild(tr)
    }
  }

  // ── Delete Modal ──────────────────────────────────────────────────

  showDeleteModal() {
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() {
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.innerHTML = `<span>${message}</span>
      <button type="button" data-action="click->flash#dismiss" class="ml-3 text-white/80 hover:text-white cursor-pointer">&times;</button>`
    document.body.prepend(toast)
  }
}
